import { parseFragment, type DefaultTreeAdapterTypes } from 'parse5'

export type HtmlAstElement = DefaultTreeAdapterTypes.Element
type HtmlAstNode = DefaultTreeAdapterTypes.Node
export type HtmlAstParentNode = DefaultTreeAdapterTypes.ParentNode
type HtmlAstTreeNode = HtmlAstNode | HtmlAstParentNode

/** Parses CMS-authored HTML as a fragment rather than creating a full document. */
export { parseFragment as parseHtmlFragment }

/** Standard HTML heading tag names in ascending level order. */
export const HTML_HEADING_TAGS: readonly string[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']

const EXCLUDED_CONTENT_TAGS = new Set(['script', 'style'])
const BLOCK_SEPARATOR_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
])

/** Narrows a parse5 tree node to an HTML element. */
const isHtmlAstElement = (node: HtmlAstTreeNode): node is HtmlAstElement => 'tagName' in node

/** Narrows a parse5 tree node to a text node. */
const isHtmlAstTextNode = (node: HtmlAstTreeNode): node is DefaultTreeAdapterTypes.TextNode => node.nodeName === '#text'

/** Returns child nodes when the supplied parse5 node can contain children. */
const getChildNodes = (node: HtmlAstTreeNode): HtmlAstNode[] => ('childNodes' in node ? node.childNodes : [])

/** Finds matching descendant elements in document order, skipping script and style subtrees. */
export const findHtmlElements = (root: HtmlAstParentNode, ...tagNames: string[]): HtmlAstElement[] => {
  const elements: HtmlAstElement[] = []

  /** Walks descendants depth-first and collects matching elements. */
  const visit = (node: HtmlAstNode) => {
    if (isHtmlAstElement(node)) {
      if (EXCLUDED_CONTENT_TAGS.has(node.tagName)) return
      if (!tagNames.length || tagNames.includes(node.tagName)) elements.push(node)
    }

    getChildNodes(node).forEach(visit)
  }

  getChildNodes(root).forEach(visit)

  return elements
}

/** Extracts descendant text while excluding script and style content. */
export const getHtmlText = (node: HtmlAstTreeNode): string => {
  if (isHtmlAstTextNode(node)) return node.value
  if (isHtmlAstElement(node) && EXCLUDED_CONTENT_TAGS.has(node.tagName)) return ''

  return getChildNodes(node).map(getHtmlText).join('')
}

/** Extracts text with line breaks around block elements and for explicit br elements. */
export const getHtmlTextWithBlockSeparators = (node: HtmlAstTreeNode): string => {
  if (isHtmlAstTextNode(node)) return node.value
  if (isHtmlAstElement(node) && EXCLUDED_CONTENT_TAGS.has(node.tagName)) return ''
  if (isHtmlAstElement(node) && node.tagName === 'br') return '\n'

  const text = getChildNodes(node).map(getHtmlTextWithBlockSeparators).join('')

  return isHtmlAstElement(node) && BLOCK_SEPARATOR_TAGS.has(node.tagName) ? `\n${text}\n` : text
}

/** Returns an element attribute value when the attribute is present. */
export const getHtmlAttribute = (element: HtmlAstElement, name: string) =>
  element.attrs.find(attribute => attribute.name === name)?.value

/** Updates an existing element attribute or appends it when missing. */
export const setHtmlAttribute = (element: HtmlAstElement, name: string, value: string) => {
  const existingAttribute = element.attrs.find(attribute => attribute.name === name)

  if (existingAttribute) {
    existingAttribute.value = value
  } else {
    element.attrs.push({ name, value })
  }
}

/** Returns only the direct element children, excluding text and comment nodes. */
export const getHtmlElementChildren = (node: HtmlAstParentNode): HtmlAstElement[] =>
  getChildNodes(node).filter(isHtmlAstElement)

/** Finds the nearest parent element whose tag name matches one of the supplied names. */
export const getClosestHtmlAncestor = (element: HtmlAstElement, ...tagNames: string[]): HtmlAstElement | undefined => {
  let ancestor: HtmlAstParentNode | null | undefined = element.parentNode

  while (ancestor) {
    if (isHtmlAstElement(ancestor) && tagNames.includes(ancestor.tagName)) return ancestor
    ancestor = 'parentNode' in ancestor ? ancestor.parentNode : undefined
  }

  return undefined
}

/** Returns following sibling elements, excluding text and comment nodes. */
export const getFollowingHtmlElementSiblings = (element: HtmlAstElement): HtmlAstElement[] => {
  const siblings = element.parentNode?.childNodes ?? []
  const elementIndex = siblings.indexOf(element)
  if (elementIndex === -1) return []

  return siblings.slice(elementIndex + 1).filter(isHtmlAstElement)
}
